import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { getAdminBlogs } from "../../admin/adminApis";
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  Check,
  Copy,
  Sparkles,
  Bookmark,
  Stethoscope,
  List,
  CheckCircle2,
  ArrowUpRight
} from "lucide-react";
import { motion, AnimatePresence, PanInfo } from "framer-motion";
import toast from "react-hot-toast";
import SEO from "../SEO";

interface Blog {
  _id: string;
  title: string;
  description: string;
  content: string;
  thumbnailUrl?: string;
  category?: string;
  createdAt?: string;
}

interface TocItem {
  id: string;
  text: string;
  number?: string;
}

export default function BlogPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [currentBlog, setCurrentBlog] = useState<Blog | null>(location.state?.blog || null);
  const [loadingCurrent, setLoadingCurrent] = useState<boolean>(!location.state?.blog);
  const [relatedBlogs, setRelatedBlogs] = useState<Blog[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [blogsPerPage, setBlogsPerPage] = useState(3);
  const [copied, setCopied] = useState(false);
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeTocId, setActiveTocId] = useState<string>("");

  /* ---------------- FETCH CURRENT BLOG IF NOT IN STATE ---------------- */
  useEffect(() => {
    let isMounted = true;

    const loadBlog = async () => {
      if (location.state?.blog && location.state.blog._id === id) {
        setCurrentBlog(location.state.blog);
        setLoadingCurrent(false);
      } else {
        setLoadingCurrent(true);
        try {
          const res = (await getAdminBlogs()) as { blogs?: Blog[] };
          if (isMounted) {
            const found = (res.blogs || []).find((b) => b._id === id);
            if (found) {
              setCurrentBlog(found);
            }
          }
        } catch (err) {
          console.error("Error fetching blog details:", err);
        } finally {
          if (isMounted) {
            setLoadingCurrent(false);
          }
        }
      }
    };

    loadBlog();
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      isMounted = false;
    };
  }, [id, location.state]);

  /* ---------------- EXTRACT TABLE OF CONTENTS ---------------- */
  useEffect(() => {
    if (!currentBlog) return;

    const rawHtml = currentBlog.content || currentBlog.description || "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = rawHtml;

    // Look for headings or numbered points like "1. Persistent Digestive Issues"
    const headingElements = tempDiv.querySelectorAll("h1, h2, h3, h4, strong");
    const extracted: TocItem[] = [];
    const seenTexts = new Set<string>();
    let counter = 0;

    headingElements.forEach((el) => {
      const text = el.textContent?.trim() || "";
      const lower = text.toLowerCase();
      if (seenTexts.has(lower)) return;

      // Check if text looks like a heading or numbered sign (e.g., "1. Persistent...")
      const numberMatch = text.match(/^(\d+)[.)]\s*(.+)/);
      if (numberMatch && text.length > 5 && text.length < 90) {
        seenTexts.add(lower);
        counter++;
        const itemId = `section-${counter}`;
        extracted.push({
          id: itemId,
          number: numberMatch[1],
          text: numberMatch[2].trim()
        });
      } else if (
        (el.tagName === "H2" || el.tagName === "H3") &&
        text.length > 4 &&
        text.length < 80 &&
        !lower.includes("consult") &&
        !lower.includes("buy vitamins")
      ) {
        seenTexts.add(lower);
        counter++;
        const itemId = `section-${counter}`;
        extracted.push({
          id: itemId,
          number: counter < 10 ? `0${counter}` : `${counter}`,
          text: text
        });
      }
    });

    setTocItems(extracted);
  }, [currentBlog]);

  /* ---------------- FETCH RELATED BLOGS ---------------- */
  useEffect(() => {
    if (!currentBlog) return;

    let isMounted = true;
    const loadRelatedBlogs = async () => {
      setLoadingRelated(true);
      try {
        const res = await getAdminBlogs();
        if (isMounted) {
          const filtered = ((res as { blogs: Blog[] }).blogs || []).filter(
            (b: Blog) => b._id !== currentBlog._id
          );
          setRelatedBlogs(filtered);
        }
      } catch (err) {
        console.error("Error fetching related blogs:", err);
        if (isMounted) setRelatedBlogs([]);
      } finally {
        if (isMounted) setLoadingRelated(false);
      }
    };
    loadRelatedBlogs();

    return () => {
      isMounted = false;
    };
  }, [currentBlog]);

  /* ---------------- RESPONSIVE CAROUSEL ---------------- */
  const getBlogsPerPage = () => {
    if (typeof window === "undefined") return 3;
    const width = window.innerWidth;
    if (width < 640) return 1;
    if (width < 1024) return 2;
    return 3;
  };

  useEffect(() => {
    setBlogsPerPage(getBlogsPerPage());
    const handleResize = () => setBlogsPerPage(getBlogsPerPage());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePrev = () => {
    if (currentIndex > 0)
      setCurrentIndex((prev) => Math.max(prev - blogsPerPage, 0));
    else
      setCurrentIndex(
        Math.floor((relatedBlogs.length - 1) / blogsPerPage) * blogsPerPage
      );
  };

  const handleNext = () => {
    if (currentIndex + blogsPerPage < relatedBlogs.length)
      setCurrentIndex((prev) => prev + blogsPerPage);
    else setCurrentIndex(0);
  };

  const handleDragStart = () => setIsDragging(true);

  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      setIsDragging(false);
      const swipeThreshold = 50;
      if (info.offset.x > swipeThreshold) handlePrev();
      else if (info.offset.x < -swipeThreshold) handleNext();
    },
    [currentIndex, blogsPerPage, relatedBlogs.length]
  );

  const visibleBlogs = relatedBlogs.slice(
    currentIndex,
    currentIndex + blogsPerPage
  );

  /* ---------------- SOCIAL SHARING ---------------- */
  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator
        .share({
          title: currentBlog?.title || "MetaFit Wellness Blog",
          text: currentBlog?.title || "Check out this article on MetaFit Wellness",
          url: url,
        })
        .catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  /* ---------------- CLEAN & PARSE CONTENT ---------------- */
  const renderCleanContent = (rawHtml: string) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = rawHtml;

    // 1. Remove unwanted Google Ad annotations
    const unwanted = tempDiv.querySelectorAll(".google-anno-skip, .google-anno-sc");
    unwanted.forEach((el) => el.remove());

    // 2. Remove duplicate/unwanted ad text phrases
    tempDiv.querySelectorAll("div, p, span").forEach((el) => {
      const text = el.textContent?.trim() || "";
      if (
        text.toLowerCase() === "buy vitamins and supplements" ||
        text.toLowerCase() === "buy vitamins"
      ) {
        el.remove();
      }
    });

    let headingIdx = 0;

    const renderNode = (node: ChildNode, key?: string | number): React.ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        return text;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const textContent = el.textContent?.trim() || "";

        // Check if element is a numbered heading, e.g. "1. Persistent Digestive Issues"
        const numHeadingMatch = textContent.match(/^(\d+)[.)]\s*(.+)/);
        const isHeaderTag = ["H1", "H2", "H3", "H4"].includes(el.tagName);

        if (numHeadingMatch && (isHeaderTag || Boolean(el.querySelector("strong")) || el.tagName === "STRONG" || (el.tagName === "P" && textContent.length < 80))) {
          headingIdx++;
          const num = numHeadingMatch[1];
          const title = numHeadingMatch[2].trim();
          const targetId = `section-${headingIdx}`;

          return (
            <div
              key={key}
              id={targetId}
              className="scroll-mt-28 my-8 pt-6 border-t border-[#2C4A3B]/10 first:border-t-0 first:pt-0"
            >
              <div className="flex items-start sm:items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-[#1E3E34] to-[#2C4A3B] text-white font-extrabold text-sm sm:text-base flex items-center justify-center shrink-0 shadow-md">
                  {num}
                </div>
                <h3 className="text-xl sm:text-2xl font-extrabold text-[#15302B] tracking-tight leading-snug">
                  {title}
                </h3>
              </div>
            </div>
          );
        }

        // Consult button callout in content
        if (
          textContent.toLowerCase().includes("consult an ayurvedic doctor") &&
          textContent.length < 50
        ) {
          return (
            <div
              key={key}
              className="my-6 p-4 sm:p-5 rounded-2xl bg-[#E8F0EC] border border-[#2C4A3B]/15 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-sm"
            >
              <div className="flex items-center gap-2.5">
                <Stethoscope className="w-5 h-5 text-[#2C4A3B] shrink-0" />
                <span className="text-sm sm:text-base font-bold text-[#15302B]">
                  Recognizing these symptoms? Consult an Ayurvedic Doctor
                </span>
              </div>
              <Link
                to="/ayurvedic-doctor"
                className="px-4 py-2 rounded-xl bg-[#2C4A3B] text-white text-xs sm:text-sm font-semibold hover:bg-[#1E3E34] transition-colors whitespace-nowrap shadow-sm flex items-center gap-1.5"
              >
                <span>Book Appointment</span>
                <ArrowUpRight size={14} />
              </Link>
            </div>
          );
        }

        switch (el.tagName) {
          case "STRONG":
          case "B":
            return (
              <strong key={key} className="font-bold text-[#15302B]">
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </strong>
            );
          case "EM":
          case "I":
            return (
              <em key={key} className="italic text-[#2E423B]">
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </em>
            );
          case "U":
            return <u key={key}>{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</u>;
          case "A":
            return (
              <a
                key={key}
                href={el.getAttribute("href") || "#"}
                className="text-[#2C4A3B] underline underline-offset-4 font-semibold hover:text-[#3F6652] transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </a>
            );
          case "BR":
            return <br key={key} />;
          case "UL":
            return (
              <ul key={key} className="my-4 space-y-2.5 pl-0 list-none">
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </ul>
            );
          case "OL":
            return (
              <ol key={key} className="my-4 space-y-2.5 pl-4 list-decimal text-[#2E423B]">
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </ol>
            );
          case "LI":
            return (
              <li
                key={key}
                className="flex items-start gap-2.5 text-sm sm:text-base text-[#2E423B] leading-relaxed"
              >
                <div className="w-2 h-2 rounded-full bg-[#2C4A3B] mt-2 shrink-0 opacity-80" />
                <div className="flex-1">{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</div>
              </li>
            );
          case "P": {
            // Filter out empty paragraphs
            if (!textContent && !el.querySelector("img")) return null;
            return (
              <p key={key} className="mb-4 text-base sm:text-[17px] leading-[1.8] text-[#2E423B]">
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </p>
            );
          }
          case "H1":
          case "H2":
            return (
              <h2
                key={key}
                className="text-2xl sm:text-3xl font-extrabold mt-8 mb-4 text-[#15302B] tracking-tight"
              >
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </h2>
            );
          case "H3":
            return (
              <h3
                key={key}
                className="text-xl sm:text-2xl font-bold mt-7 mb-3 text-[#15302B] tracking-tight"
              >
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </h3>
            );
          case "H4":
          case "H5":
          case "H6":
            return (
              <h4 key={key} className="text-lg sm:text-xl font-bold mt-6 mb-2.5 text-[#15302B]">
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </h4>
            );
          case "BLOCKQUOTE":
            return (
              <blockquote
                key={key}
                className="border-l-4 border-[#2C4A3B] bg-[#F2ECE1]/50 pl-5 pr-4 py-3.5 my-6 rounded-r-2xl italic text-[#15302B] font-medium shadow-sm"
              >
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </blockquote>
            );
          case "TABLE":
            return (
              <div key={key} className="overflow-x-auto my-6 rounded-2xl border border-gray-200">
                <table className="table-auto w-full text-left border-collapse">
                  {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
                </table>
              </div>
            );
          case "TR":
            return <tr key={key} className="border-b border-gray-200">{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</tr>;
          case "TH":
            return (
              <th key={key} className="px-4 py-3 bg-[#E8F0EC] text-[#15302B] font-bold text-sm">
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </th>
            );
          case "TD":
            return (
              <td key={key} className="px-4 py-3 text-sm text-[#2E423B]">
                {Array.from(el.childNodes).map((n, i) => renderNode(n, i))}
              </td>
            );
          case "HR":
            return <div key={key} className="my-8 border-t border-[#2C4A3B]/10" />;
          case "IMG": {
            const src = el.getAttribute("src") || "";
            if (!src) return null;
            return (
              <div
                key={key}
                className="my-6 rounded-2xl overflow-hidden shadow-md bg-gray-50 border border-gray-100 max-h-[380px]"
              >
                <img
                  src={src}
                  alt={el.getAttribute("alt") || currentBlog?.title || "MetaFit Article"}
                  className="w-full h-auto max-h-[380px] object-cover"
                  loading="lazy"
                />
              </div>
            );
          }
          default:
            return <div key={key}>{Array.from(el.childNodes).map((n, i) => renderNode(n, i))}</div>;
        }
      }
      return null;
    };

    return Array.from(tempDiv.childNodes).map((node, i) => renderNode(node, i));
  };

  const extractPlainText = (htmlString: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    return (tempDiv.textContent || tempDiv.innerText || "").replace(/\s+/g, " ").trim().slice(0, 160);
  };

  /* ---------------- LOADING STATE ---------------- */
  if (loadingCurrent) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-8 bg-white">
        <div className="w-10 h-10 border-4 border-[#2C4A3B]/20 border-t-[#2C4A3B] rounded-full animate-spin mb-4" />
        <p className="text-[#3B5750] font-semibold text-sm">Loading article...</p>
      </div>
    );
  }

  /* ---------------- BLOG NOT FOUND ---------------- */
  if (!currentBlog) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center bg-white">
        <SEO
          title="Blog Post Not Found | MetaFit Wellness"
          description="The requested wellness blog post could not be found."
          robots="noindex, follow"
        />
        <div className="bg-white p-8 sm:p-10 rounded-3xl border border-[#2C4A3B]/10 shadow-sm max-w-md w-full">
          <Bookmark className="w-12 h-12 text-[#C69248] mx-auto mb-3 opacity-80" />
          <h1 className="text-2xl font-bold text-[#15302B] mb-2">Article Not Found</h1>
          <p className="text-sm text-[#4A5D57] mb-6">The article you are looking for may have been moved or updated.</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#2C4A3B] text-white rounded-xl text-sm font-semibold hover:bg-[#1E3E34] transition-all shadow-md"
          >
            <ArrowLeft size={16} /> Back to Blog Listing
          </Link>
        </div>
      </div>
    );
  }

  const excerpt = extractPlainText(currentBlog.description || currentBlog.content) || "Discover holistic wellness insights on MetaFit Wellness.";
  const canonicalUrl = `https://www.metafitwellness.com/blog/${currentBlog._id}`;
  const blogImage = currentBlog.thumbnailUrl || "https://www.metafitwellness.com/metafit.webp";
  const rawText = extractPlainText(currentBlog.content || currentBlog.description);
  const readingTime = Math.max(1, Math.ceil(rawText.split(" ").length / 180));

  return (
    <div className="min-h-screen bg-white text-gray-900 pb-16 selection:bg-[#2C4A3B] selection:text-white">
      <SEO
        title={`${currentBlog.title} | MetaFit Wellness`}
        description={excerpt}
        image={blogImage}
        url={canonicalUrl}
        canonical={canonicalUrl}
        type="article"
      />

      {/* Main Container */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 md:pt-10">
        
        {/* Article Headline & Meta */}
        <header className="max-w-3xl mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#E8F0EC] text-[#2C4A3B] border border-[#2C4A3B]/15 mb-3 shadow-xs">
            <Sparkles className="w-3.5 h-3.5 text-[#C69248]" />
            <span>{currentBlog.category || "Wellness & Ayurveda"}</span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl font-extrabold text-[#15302B] leading-tight tracking-tight mb-4">
            {currentBlog.title}
          </h1>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-[#4A5D57]">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#2C4A3B] text-white flex items-center justify-center font-bold text-xs shadow-xs">
                M
              </div>
              <span className="font-semibold text-[#15302B]">MetaFit Editorial Team</span>
            </div>

            {currentBlog.createdAt && (
              <>
                <span className="text-gray-300">•</span>
                <div className="flex items-center gap-1">
                  <Calendar size={13} className="text-[#C69248]" />
                  <span>
                    {new Date(currentBlog.createdAt).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </>
            )}

            <span className="text-gray-300">•</span>
            <div className="flex items-center gap-1">
              <Clock size={13} className="text-[#C69248]" />
              <span>{readingTime} min read</span>
            </div>

            <span className="text-gray-300">•</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
              <CheckCircle2 size={12} />
              Verified Guide
            </span>

            <span className="text-gray-300">•</span>
            <button
              onClick={handleShare}
              type="button"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E8F0EC] border border-[#2C4A3B]/15 text-xs font-semibold text-[#2C4A3B] hover:bg-[#d8e6df] transition-all cursor-pointer shadow-xs"
              title="Share article"
            >
              {copied ? <Check size={13} className="text-emerald-700" /> : <Share2 size={13} />}
              <span>{copied ? "Copied" : "Share"}</span>
            </button>
          </div>
        </header>

        {/* Featured Hero Image - Balanced Landscape (max-h-360px so content is visible right away) */}
        {currentBlog.thumbnailUrl && (
          <div className="mb-8 rounded-3xl overflow-hidden shadow-lg border border-white/80 aspect-[16/9] sm:aspect-[21/9] max-h-[380px] bg-gray-100 relative group">
            <img
              src={currentBlog.thumbnailUrl}
              alt={currentBlog.title}
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
          </div>
        )}

        {/* 2-COLUMN LAYOUT: Content (8 cols) + Sticky Sidebar (4 cols) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          
          {/* Main Article Content */}
          <article className="lg:col-span-8 bg-white rounded-3xl p-6 sm:p-8 md:p-10 shadow-sm border border-[#2C4A3B]/10 overflow-hidden">
            
            {/* The Cleaned & Formatted Content */}
            <div className="article-body">
              {renderCleanContent(currentBlog.content || currentBlog.description)}
            </div>

            {/* In-Article CTA Banner */}
            <div className="mt-10 p-6 sm:p-7 rounded-2xl bg-gradient-to-br from-[#1E3E34] via-[#24483C] to-[#15302B] text-white shadow-xl relative overflow-hidden flex flex-col sm:flex-row items-center justify-between gap-5">
              <div className="space-y-1 text-center sm:text-left">
                <p className="text-[11px] uppercase tracking-wider text-[#D4A373] font-bold">Personalized Healing</p>
                <h4 className="text-lg sm:text-xl font-bold text-white">Need an Ayurvedic Consultation?</h4>
                <p className="text-xs text-[#E8E1CF] max-w-sm">
                  Get personalized root-cause analysis, dosha balancing and diet therapy from verified doctors.
                </p>
              </div>
              <Link
                to="/ayurvedic-doctor"
                className="px-4 py-2.5 bg-[#C69248] text-white rounded-xl text-xs sm:text-sm font-bold shadow-md hover:bg-[#B76E00] transition-colors whitespace-nowrap cursor-pointer shrink-0"
              >
                Book Consultation
              </Link>
            </div>

            {/* Author Footer Card */}
            <div className="mt-8 pt-6 border-t border-[#2C4A3B]/10 flex flex-col sm:flex-row items-center sm:items-start gap-4 bg-white p-5 rounded-2xl">
              <div className="w-12 h-12 rounded-2xl bg-[#2C4A3B] text-white flex items-center justify-center font-bold text-base shadow-sm shrink-0">
                M
              </div>
              <div className="text-center sm:text-left space-y-1">
                <h5 className="font-bold text-[#15302B] text-sm">MetaFit Wellness Editorial Team</h5>
                <p className="text-xs text-[#4A5D57] leading-relaxed">
                  Our articles are researched and reviewed in collaboration with certified yoga therapists, Ayurvedic practitioners, and holistic nutritionists.
                </p>
              </div>
            </div>

            {/* Share Article Row */}
            <div className="mt-6 pt-5 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#15302B]">Share:</span>
                <button
                  onClick={handleShare}
                  className="px-3 py-1.5 rounded-lg bg-white border border-[#2C4A3B]/15 text-[#2C4A3B] text-xs font-semibold hover:bg-[#E8F0EC] transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <Copy size={13} />
                  <span>Copy Link</span>
                </button>
                <button
                  onClick={() => {
                    const url = encodeURIComponent(window.location.href);
                    const text = encodeURIComponent(`Read "${currentBlog.title}" on MetaFit: `);
                    window.open(`https://api.whatsapp.com/send?text=${text}${url}`, "_blank");
                  }}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors shadow-xs"
                >
                  WhatsApp
                </button>
              </div>

              <Link
                to="/blog"
                className="text-xs font-semibold text-[#2C4A3B] hover:underline"
              >
                ← Back to all wellness insights
              </Link>
            </div>

          </article>

          {/* Sticky Sidebar (Desktop only) */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-20">
            
            {/* Table of Contents Widget (if headings exist) */}
            {tocItems.length > 0 && (
              <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-sm border border-[#2C4A3B]/10">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                  <List className="w-4 h-4 text-[#2C4A3B]" />
                  <h4 className="font-bold text-sm text-[#15302B] tracking-tight">Table of Contents</h4>
                </div>

                <div className="flex flex-col space-y-1 max-h-72 overflow-y-auto pr-1">
                  {tocItems.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className="group flex items-start gap-2.5 py-1.5 px-2.5 rounded-xl text-xs text-[#3B5750] hover:text-[#15302B] hover:bg-white transition-all"
                    >
                      <span className="font-bold text-[#C69248] shrink-0 text-[11px] group-hover:text-[#2C4A3B]">
                        {item.number ? `${item.number}.` : "•"}
                      </span>
                      <span className="line-clamp-1 leading-snug group-hover:underline">
                        {item.text}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Doctor Consultation Card */}
            <div className="bg-gradient-to-br from-[#1E3E34] via-[#24483C] to-[#15302B] rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center text-[#D4A373]">
                  <Stethoscope size={18} />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#D4A373]">
                  Doctor Consultation
                </span>
              </div>

              <h4 className="font-extrabold text-lg text-white mb-2 leading-snug">
                Talk to an Ayurvedic Doctor
              </h4>

              <p className="text-xs text-[#E8E1CF] leading-relaxed mb-4">
                Get a root-cause diagnosis for chronic digestive, skin, hormonal, or fatigue issues with natural herbal remedies.
              </p>

              <Link
                to="/ayurvedic-doctor"
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-[#C69248] hover:bg-[#B76E00] text-white text-xs font-bold transition-all shadow-md"
              >
                <span>Find Ayurvedic Doctor</span>
                <ArrowUpRight size={14} />
              </Link>
            </div>

            {/* Trending Articles Widget */}
            {relatedBlogs.length > 0 && (
              <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-sm border border-[#2C4A3B]/10">
                <h4 className="font-bold text-sm text-[#15302B] mb-3 pb-2 border-b border-gray-100">
                  Popular Reads
                </h4>
                <div className="space-y-3">
                  {relatedBlogs.slice(0, 3).map((rb) => (
                    <Link
                      key={rb._id}
                      to={`/blog/${rb._id}`}
                      state={{ blog: rb }}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="group flex items-center gap-3 hover:bg-white p-1.5 rounded-xl transition-all"
                    >
                      <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                        <img
                          src={rb.thumbnailUrl || "/metalogo.webp"}
                          alt={rb.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-[#15302B] group-hover:text-[#2C4A3B] line-clamp-2 leading-snug">
                          {rb.title}
                        </p>
                        <span className="text-[10px] text-[#C69248] font-medium mt-0.5 inline-block">
                          Read more →
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

          </aside>

        </div>

      </main>

      {/* ---------------- RELATED ARTICLES SLIDER / GRID ---------------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16 pt-10 border-t border-[#2C4A3B]/10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-[#C69248]">More From MetaFit</span>
            <h3 className="text-xl sm:text-2xl font-extrabold text-[#15302B] tracking-tight mt-0.5">
              Related Articles
            </h3>
          </div>

          {relatedBlogs.length > blogsPerPage && (
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                className="p-2 rounded-xl bg-white border border-[#2C4A3B]/15 text-[#2C4A3B] hover:bg-[#E8F0EC] shadow-xs transition-colors cursor-pointer"
                aria-label="Previous"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={handleNext}
                className="p-2 rounded-xl bg-white border border-[#2C4A3B]/15 text-[#2C4A3B] hover:bg-[#E8F0EC] shadow-xs transition-colors cursor-pointer"
                aria-label="Next"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        {loadingRelated ? (
          <p className="text-xs text-gray-500">Loading related blogs...</p>
        ) : relatedBlogs.length === 0 ? (
          <p className="text-xs text-gray-400">No related articles found.</p>
        ) : (
          <div className="relative" ref={containerRef}>
            <motion.div
              className="overflow-hidden"
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              drag={blogsPerPage === 1 ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.3 }}
                  className={`grid grid-cols-1 ${blogsPerPage >= 2 ? "sm:grid-cols-2" : ""} ${
                    blogsPerPage >= 3 ? "lg:grid-cols-3" : ""
                  } gap-6`}
                >
                  {visibleBlogs.map((rb) => (
                    <Link
                      key={rb._id}
                      to={`/blog/${rb._id}`}
                      state={{ blog: rb }}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="group bg-white rounded-2xl overflow-hidden shadow-xs hover:shadow-xl border border-[#2C4A3B]/10 hover:border-[#2C4A3B]/30 transition-all duration-300 flex flex-col hover:-translate-y-1"
                    >
                      <div className="h-44 bg-gray-100 overflow-hidden relative">
                        <img
                          src={rb.thumbnailUrl || "/metalogo.webp"}
                          alt={rb.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {rb.category && (
                          <div className="absolute top-3 left-3">
                            <span className="bg-white/95 text-[#2C4A3B] px-2.5 py-0.5 rounded-full text-[11px] font-bold shadow-xs">
                              {rb.category}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-1.5 text-[11px] text-[#556963] mb-1.5">
                            <Calendar size={12} className="text-[#C69248]" />
                            <span>
                              {rb.createdAt
                                ? new Date(rb.createdAt).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })
                                : "Recent"}
                            </span>
                          </div>
                          <h4 className="font-bold text-[#15302B] group-hover:text-[#2C4A3B] transition-colors line-clamp-2 text-base leading-snug">
                            {rb.title}
                          </h4>
                        </div>
                        <span className="text-xs text-[#2C4A3B] font-semibold mt-3 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                          Read article →
                        </span>
                      </div>
                    </Link>
                  ))}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </section>
    </div>
  );
}
